import { useCallback, useEffect, useState } from 'react';

/**
 * Hide-to-edge-tab — the USER's own dismiss for a floating launcher.
 *
 * Route suppression is the *developer* deciding a launcher must not exist on a page. This is the
 * *user* deciding — "get this bubble off my screen" — which every launcher should support,
 * especially on a phone where each floating button costs real content.
 *
 * The one non-negotiable in the pattern: **while hidden, everything un-renders EXCEPT a
 * translucent edge tab.** Rendering nothing at all would strand the user with no way back, and
 * for the navigation launcher that means stranding them with no navigation. The tab is the
 * guarantee, so it is never gated behind anything.
 *
 * The reset broadcast un-hides as well, so the footer's "↻ Reset floating panels" recovers a
 * launcher whose edge tab the user can't find.
 *
 * vault/how-to/draggable-floating-launchers.md § Hide-to-edge-tab.
 */
export function useHideToEdge(storageKey: string, resetEvent = 'tms:reset-floating-panels') {
  const [hidden, setHiddenState] = useState<boolean>(() => {
    try { return localStorage.getItem(storageKey) === '1'; } catch { return false; }
  });

  const setHidden = useCallback((next: boolean) => {
    setHiddenState(next);
    try { localStorage.setItem(storageKey, next ? '1' : '0'); } catch { /* private browsing */ }
  }, [storageKey]);

  useEffect(() => {
    const onReset = () => {
      setHiddenState(false);
      // Self-clearing is required, not optional: the reset dispatcher clears storage too, but a
      // listener that only changed state would let the hidden flag come back on reload.
      try { localStorage.removeItem(storageKey); } catch { /* noop */ }
    };
    window.addEventListener(resetEvent, onReset);
    return () => window.removeEventListener(resetEvent, onReset);
  }, [storageKey, resetEvent]);

  return { hidden, setHidden };
}
